import {useMutation} from '@tanstack/react-query';
import {AxiosError} from 'axios';
import {APIResponseType, HttpResponse, APIResponse, UpdateTestimonialDTO} from './models';
import {apiClient} from '../foundation/apiClient';
import {useEventBatching} from './events';

const updateTestimonialAPI = async (requestData: UpdateTestimonialDTO) => {
    const path = 'testimonial/update';
    const data = await apiClient.post(path, requestData);
    return data.data;
};

interface MutationParams {
    updateTestimonialDTO: UpdateTestimonialDTO;
    cb?: VoidFunction;
}

export const useUpdateNewTestimonial = () => {
    const {trackEvent} = useEventBatching();

    const {mutate, ...rest} = useMutation({
        mutationFn: (data: UpdateTestimonialDTO) => updateTestimonialAPI(data),
        retry: false,
    });

    const addTestimonial = ({
        data,
        throwSnackbarMessage,
    }: {
        data: MutationParams;
        throwSnackbarMessage: (message: string) => void;
    }) => {
        mutate(data.updateTestimonialDTO, {
            onSuccess: async (response) => {
                if (response.type === APIResponseType.SuccessfulString) {
                    const successMessage = (response.data as HttpResponse).message;
                    throwSnackbarMessage(successMessage);
                    trackEvent({
                        eventName: 'ADDING_TESTIMONIAL_SUCCEEDED',
                        props: {
                            ...{
                                message: successMessage,
                            },
                            ...data.updateTestimonialDTO,
                        },
                    });
                    data.cb?.();
                } else if (response.type === APIResponseType.ErrorString) {
                    const errorMessage = (response.data as HttpResponse).message;
                    throwSnackbarMessage((response.data as HttpResponse).message);
                    trackEvent({
                        eventName: 'ADDING_TESTIMONIAL_FAILED',
                        props: {
                            ...{
                                message: errorMessage,
                            },
                            ...data.updateTestimonialDTO,
                        },
                    });
                }
            },
            onError: (error) => {
                if (error.name === 'AxiosError') {
                    const err = error as AxiosError;
                    const message = err.response?.data as APIResponse;
                    if (message.type === APIResponseType.ErrorString) {
                        const httpResponse = message.data as HttpResponse;
                        throwSnackbarMessage(httpResponse.message);
                        trackEvent({
                            eventName: 'ADDING_TESTIMONIAL_FAILED',
                            props: {
                                ...{
                                    errorMessage: httpResponse.message.toString(),
                                },
                                ...data.updateTestimonialDTO,
                            },
                        });
                    }
                } else {
                    throwSnackbarMessage?.(error.message);
                    trackEvent({
                        eventName: 'ADDING_TESTIMONIAL_FAILED',
                        props: {
                            ...{
                                errorMessage: error.message.toString(),
                            },
                            ...data.updateTestimonialDTO,
                        },
                    });
                }
            },
        });
    };

    return {addTestimonial, ...rest};
};
